import { useData, useSession } from "contexts";
import { createDefaultCategory, createDefaultMenu, IMenu, IUser } from "interfaces";
import { useEffect, useState } from "react";
import { StringOrOptionType } from "types";

export const useMenu = (initialMenu: IMenu[], readonly: boolean, createdBy?: string, onUpdate?: (menu: IMenu[]) => void) => {
  const { user } = useSession();
  const { configurations: defaultConfigurations } = useData();

  const currentUser = user?.id || "";
  const currentOwner = createdBy || currentUser;

  const [menuData, setMenuData] = useState<IMenu[]>(initialMenu);
  const [categories, setCategories] = useState<string[]>([]);
  const [configurations, setConfigurations] = useState<StringOrOptionType[]>([]);

  useEffect(() => {
    setCategories(initialMenu.map(item => item.configuration._id));
    setMenuData(initialMenu);
  }, [initialMenu]);

  useEffect(() => {
    setConfigurations(defaultConfigurations
      .filter((item) => !categories.includes(item._id))
      .map((item) => ({ label: item.name, value: item._id })))
  }, [categories])

  useEffect(() => initializeItems(), [readonly]);

  const updateMenu = (newData: IMenu[]) => {
    setMenuData(newData);
    onUpdate?.(newData);
  };

  const isReadyOnly = (id: string, isNew: boolean) => currentUser === id || isNew ? false : true;

  const initializeItems = () => {
    if (readonly) {
      const updatedData = initialMenu.map((menu) => ({
        ...menu,
        readonly: isReadyOnly(currentOwner, menu.new),
        categories: menu.categories.map((category) => ({
          ...category,
          readonly: isReadyOnly(category.suggested_by._id, menu.new),
        })),
      }));
      updateMenu(updatedData);
    } else setMenuData(initialMenu);
  };

  const handleDelete = (category: string) => {
    const newCategories = categories.filter((cat) => cat !== category);
    const newData = menuData.filter(
      (item) => item.configuration._id !== category
    );
    setCategories(newCategories);
    updateMenu(newData);
  };

  const handleSelect = (category: StringOrOptionType) => {
    const newCategory =
      typeof category === "string" ? category : (category.value as string);
    const newMenu = {
      ...createDefaultMenu(readonly),
      configuration: defaultConfigurations.find((c) => c._id === newCategory),
    };
    const newCategories = [...categories, newCategory];
    const newData = [...menuData, newMenu];
    setCategories(newCategories);
    updateMenu(newData);
  };

  const handleAddItem = (menuIndex: number) => {
    const newCategory = {
      ...createDefaultCategory(),
      suggested_by: { _id: currentUser } as IUser,
    }
    const updatedItems = menuData.map((menu, index) =>
      index === menuIndex
        ? {
          ...menu,
          categories: [...menu.categories, newCategory],
        }
        : menu
    );
    updateMenu(updatedItems);
  };

  const handleRemoveItem = (menuIndex: number, id: string) => {
    const updatedItems = menuData.map((menu, index) => {
      return index === menuIndex
        ? {
          ...menu,
          categories: menu.categories.filter((cat) => cat._id !== id),
        }
        : menu
    });
    updateMenu(updatedItems);
  };

  const handleChange = (menuIndex: number, id: string, value: string) => {
    const updatedItems = menuData.map((menu, index) =>
      index === menuIndex
        ? {
          ...menu,
          categories: menu.categories.map((category, i) =>
            category._id === id ? { ...category, name: value } : category
          ),
        }
        : menu
    );
    updateMenu(updatedItems);
  };

  const handleCounterChange = (count: number, menuIndex: number) => {
    const updatedItems = menuData.map((menu, index) =>
      index === menuIndex ? { ...menu, count } : menu
    );
    updateMenu(updatedItems);
  };

  return {
    user: currentUser,
    menuData,
    categories,
    configurations,
    handleSelect,
    handleDelete,
    handleAddItem,
    handleRemoveItem,
    handleChange,
    handleCounterChange,
  };
};